import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormError, PageHeader, ProgressBar, StatusBadge } from "@/components/common/ui-bits";
import { AlertLog } from "@/components/budgets/AlertLog";
import { api, errMsg } from "@/lib/api";
import { BUDGET_ERROR, centsToInput, isValidBudget, money, monthLong } from "@/lib/format";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const MoneyInput = ({ value, onChange, testId, placeholder }) => (
  <div className="flex items-center rounded-xl border border-input bg-card h-10 px-3 focus-within:ring-2 focus-within:ring-ring/30">
    <span className="text-muted-foreground font-num text-sm">$</span>
    <input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full min-w-0 bg-transparent pl-1.5 font-num text-sm outline-none" data-testid={testId} />
  </div>
);

function Figures({ row }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      <div><div className="text-[11px] text-muted-foreground">Budget</div><div className="font-num font-semibold">{row.budget_cents == null ? "—" : money(row.budget_cents)}</div></div>
      <div><div className="text-[11px] text-muted-foreground">Spent</div><div className="font-num font-semibold text-rose-600" data-testid="budget-actual">{money(row.actual_cents)}</div></div>
      <div><div className="text-[11px] text-muted-foreground">Remaining</div><div className="font-num font-semibold" data-testid="budget-remaining">{row.remaining_cents == null ? "—" : money(row.remaining_cents)}</div></div>
      <div><div className="text-[11px] text-muted-foreground">% used</div><div className="font-num font-semibold" data-testid="budget-percent">{row.percent_used == null ? "—" : `${row.percent_used}%`}</div></div>
    </div>
  );
}

function CategoryBudget({ row, onSaved }) {
  const [budget, setBudget] = useState(centsToInput(row.budget_cents));
  const [bench, setBench] = useState(centsToInput(row.benchmark_cents));
  const [error, setError] = useState("");
  const id = slug(row.name);

  const save = async () => {
    if (!isValidBudget(budget)) return setError(BUDGET_ERROR);
    if (!isValidBudget(bench)) return setError("Enter a benchmark of 0 or more");
    try {
      await api.put(`/budgets/categories/${row.category_id}`, { monthly_budget: budget, benchmark: bench });
      setError(""); toast.success(`${row.name} budget saved`); onSaved();
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <div className="surface p-4 sm:p-5 fade-up" data-testid={`budget-item-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-heading font-semibold">
          {row.name}
          {row.is_archived && <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground align-middle" data-testid={`budget-archived-${id}`}>Archived</span>}
        </h3>
        {row.status !== "none" && <StatusBadge status={row.status} testId={`budget-status-${id}`} />}
      </div>
      <Figures row={row} />
      {row.budget_cents ? <div className="mt-3"><ProgressBar percent={row.percent_used} status={row.status} /></div> : null}
      {row.benchmark_cents != null && (
        <div className="mt-3 text-xs rounded-lg bg-accent text-accent-foreground px-3 py-2 inline-block" data-testid={`budget-benchmark-${id}`}>
          Industry benchmark: <b className="font-num">{money(row.benchmark_cents)}</b> / month · you spent <b className="font-num">{money(row.actual_cents)}</b>
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div><label className="text-[11px] text-muted-foreground">Monthly budget</label>
          <MoneyInput value={budget} onChange={(v) => { setBudget(v); setError(""); }} placeholder="Not set" testId={`budget-input-${id}`} /></div>
        <div><label className="text-[11px] text-muted-foreground">Industry benchmark (optional)</label>
          <MoneyInput value={bench} onChange={(v) => { setBench(v); setError(""); }} placeholder="None" testId={`benchmark-input-${id}`} /></div>
        <Button onClick={save} className="h-10 rounded-xl" data-testid={`budget-save-${id}`}>Save</Button>
      </div>
      {error && <div className="mt-3"><FormError message={error} testId={`budget-error-${id}`} /></div>}
    </div>
  );
}

function OverallTarget({ overall, onSaved }) {
  const [value, setValue] = useState(centsToInput(overall.budget_cents));
  const [error, setError] = useState("");
  const save = async () => {
    if (!isValidBudget(value)) return setError(BUDGET_ERROR);
    try {
      await api.put("/budgets/overall", { monthly_target: value });
      setError(""); toast.success("Overall target saved"); onSaved();
    } catch (e) { setError(errMsg(e)); }
  };
  return (
    <div className="surface p-5 sm:p-6 mb-6 border-primary/30 fade-up" data-testid="budget-overall">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">Overall monthly spending target</h2>
          <p className="text-xs text-muted-foreground">Total expenses this month vs your target</p>
        </div>
        {overall.status !== "none" && <StatusBadge status={overall.status} testId="budget-overall-status" />}
      </div>
      <Figures row={overall} />
      {overall.budget_cents ? <div className="mt-3"><ProgressBar percent={overall.percent_used} status={overall.status} /></div> : null}
      <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="sm:w-64"><label className="text-[11px] text-muted-foreground">Monthly target</label>
          <MoneyInput value={value} onChange={(v) => { setValue(v); setError(""); }} placeholder="Not set" testId="budget-overall-target-input" /></div>
        <Button onClick={save} className="h-10 rounded-xl" data-testid="budget-overall-save">Save target</Button>
      </div>
      {error && <div className="mt-3"><FormError message={error} testId="budget-overall-error" /></div>}
    </div>
  );
}

export default function BudgetsPage() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/budgets").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow={monthLong(data.month)} title="Budgets" subtitle="Budgets repeat every month. Alerts show at 80% (approaching) and 100% (over). Leave blank or 0 for no alert." testId="budgets-title" />
      {data.alerts.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="budgets-alerts">
          {data.alerts.map((a) => (
            <span key={a.category_id} className={`rounded-full border px-3 py-1 text-xs font-semibold ${a.status === "over" ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-amber-100 text-amber-900 border-amber-300"}`} data-testid={`budgets-alert-${slug(a.name)}`}>
              {a.status === "over" ? "Over budget" : "Approaching budget"}: {a.name} ({a.percent_used}%)
            </span>
          ))}
        </div>
      )}
      <OverallTarget key={`o-${data.overall.budget_cents}`} overall={data.overall} onSaved={load} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {data.categories.map((r) => <CategoryBudget key={`${r.category_id}-${r.budget_cents}-${r.benchmark_cents}`} row={r} onSaved={load} />)}
      </div>
      <AlertLog refreshKey={data} />
    </div>
  );
}
